// Single-character ellipsis — narrower than "...". Lives here rather
// than in `IdCell` because the pure function is the lower layer: it
// produces the elided *string*, and the component only renders it.
export const ELLIPSIS = '…';

// A UUID (any version) exactly as the backend serialises it: 8-4-4-4-12
// hex, hyphenated. The last group is captured because it is the part
// that survives truncation.
//
// This is a POSITIVE match on purpose. The backend's
// `contexts/commerce/http/id-shape.ts` can get away with an exclusion
// rule (`/^\d+$/` means Shopify, everything else means UUID) because it
// resolves inside a closed world of three commerce tables. The frontend
// renders an open world of ids — Onfleet worker ids
// (`rJOclSeGqZOm5uHcw*cxSzOW`), `short_id`s, driver ids — and an
// exclusion rule would classify those as UUIDs and cut off their
// leading half, producing a string that still looks like a legal id.
export const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-([0-9a-f]{12})$/i;

/**
 * How much of an id may be dropped.
 *
 * - `number` — a display budget: shorten anything longer than N.
 * - `false`  — never shorten (the full id is the point: an export, a
 *              field an operator diffs character by character).
 * - `'uuid'` — shorten only what stays honest when shortened. A UUID's
 *              trailing group is a boundary of the format and identifies
 *              the row; every other shape on a detail surface is an
 *              EXTERNAL key (Shopify, Onfleet) whose head still reads as
 *              a legal key for a different record, so it stays whole.
 *              This is the DETAIL-surface budget.
 */
export type IdBudget = number | false | 'uuid';

export interface ShortenedId {
  /** The display string, ellipsis included. */
  text: string;
  /**
   * Which end was dropped. Not consumed by any renderer — it exists so
   * tests can assert *which branch ran* rather than only the resulting
   * text, which can match by coincidence.
   */
  elided: 'head' | 'tail' | 'none';
}

/**
 * Shorten an id for display, choosing which end to keep from the id's
 * own shape. The single source of that decision for the whole frontend.
 *
 * - UUID → keep the trailing 12-char group. Internal primary keys are
 *   UUIDv7, whose leading 12 chars are a millisecond timestamp: rows
 *   written in the same request are indistinguishable by their head
 *   (prod `resource_audit_logs`, 27,417 rows: 2,513 collide on the
 *   first 12 chars, 0 on the last 12).
 * - anything else → keep the leading `budget` chars, as before. Shopify
 *   ids and Onfleet ids carry no timestamp prefix and collide zero
 *   times on their head in prod.
 *
 * `budget` is deliberately ignored on the UUID branch: the trailing
 * group is a natural boundary of the format, and half of it
 * (`e94cf39b`) is neither more informative nor recognisable. Honouring
 * the budget there would also render the same UUID at different lengths
 * on different surfaces — the inconsistency this function removes.
 *
 * `budget === false` and `budget < 1` both mean "no truncation"; a budget
 * of 0 would otherwise render just `…`. `budget === 'uuid'` (detail
 * surfaces) reaches the UUID branch and stops before the head branch —
 * see `IdBudget`.
 */
export function shortenId(full: string, budget: IdBudget): ShortenedId {
  if (budget === false || (typeof budget === 'number' && budget < 1)) {
    return { text: full, elided: 'none' };
  }

  const uuidTail = UUID_SHAPE.exec(full)?.[1];
  if (uuidTail) {
    return { text: `${ELLIPSIS}${uuidTail}`, elided: 'tail' };
  }

  // The head branch is a LENGTH decision, and `'uuid'` has no length to
  // compare against — by design: it shortens by shape only.
  if (budget !== 'uuid' && full.length > budget) {
    return { text: `${full.slice(0, budget)}${ELLIPSIS}`, elided: 'head' };
  }

  return { text: full, elided: 'none' };
}
